import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import InventoryAPI from "../../services/inventoryService";
import {
  getServiceItemTemplates,
  createServiceItemTemplate,
  updateServiceItemTemplate,
  deleteServiceItemTemplate
} from "../../services/servicesApiV2";

/**
 * ProductItemTemplateModal
 *
 * Manages material item templates (type='material') on a master service.
 * Each row: Label | Brand | Product | Qty (sell units) | Delete
 *
 * Units: a product is stocked/used in its BASE measure (ml, sq ft, each) but
 * handed over / quantified in whole SELL units (bottle, roll, each), where
 * 1 sell unit = base_units_per_sell_unit base units (factor; null ⇒ 1).
 * The user enters Qty in SELL units; we store expected_qty in BASE units.
 *
 * Bundle support: a material row can be flagged as a bundled/included product
 * (e.g. a cleaner included with a Ceramic service). The per-row "Bundled" toggle
 * is PERSISTED as is_bundle on the task — it both widens the product list past
 * the service category (so a cross-category product can be picked) AND marks the
 * item as an included takeaway (drives whole-unit auto-resolve at capture and the
 * "Includes …" line at checkout). Detection must NOT rely on category difference:
 * an included product can share the service's category. Legacy rows with no
 * is_bundle fall back to the old cross-category heuristic.
 */
const ProductItemTemplateModal = ({
  show,
  onHide,
  masterServiceId,
  installerId,
  categoryName,
  serviceName,
  onTemplatesChanged
}) => {
  const [items, setItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);   // every product (for bundle/all)
  const [units, setUnits] = useState([]);
  const [stockLevels, setStockLevels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [originalItems, setOriginalItems] = useState([]);

  useEffect(() => {
    if (show && masterServiceId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, masterServiceId]);

  // ---- unit / packaging helpers --------------------------------------------
  const unitName = (unitId) => {
    const u = (units || []).find(x => String(x.unit_id) === String(unitId));
    return u ? (u.unit_code || u.unit_name || '') : '';
  };

  // Base-units-per-sell-unit factor (+ unit labels) for a product.
  // Null/absent factor ⇒ 1 (sell unit == base unit; no-op for film).
  const packaging = (productId) => {
    const p = (allProducts || []).find(x => String(x.product_id || x.id) === String(productId));
    const factor = parseFloat(p?.base_units_per_sell_unit) || 1;
    const baseName = unitName(p?.unit_id) || '';
    const sellName = (p?.sell_unit_id && unitName(p.sell_unit_id)) || baseName;
    return { factor, baseName, sellName, product: p };
  };

  // Brand list + brand→products map for a row's product pool (category-scoped
  // by default; widened to all products when the row is a bundle/"any category").
  const poolFor = (showAll) => {
    const pool = (allProducts || []).filter(p => showAll ? true : (p.category_name === categoryName));
    const sorted = [...pool].sort((a, b) => {
      const ba = (a.brand || '').toLowerCase(), bb = (b.brand || '').toLowerCase();
      if (ba !== bb) return ba.localeCompare(bb);
      return (a.product_name || a.name || '').toLowerCase().localeCompare((b.product_name || b.name || '').toLowerCase());
    });
    const brands = [...new Set(sorted.map(p => p.brand).filter(Boolean))].sort();
    const byBrand = {};
    sorted.forEach(p => { const b = p.brand || ''; (byBrand[b] = byBrand[b] || []).push(p); });
    return { brands, byBrand };
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [templates, stockData, allocatedData, unitsData] = await Promise.all([
        getServiceItemTemplates(masterServiceId),
        InventoryAPI.getStockLevels(installerId),
        InventoryAPI.getAllocatedInventory(installerId).catch(() => []),
        InventoryAPI.getUnitsOfMeasure().catch(() => [])
      ]);

      const allProds = stockData || [];
      setAllProducts(allProds);
      setUnits(unitsData || []);

      // Build stock lookup: { productId: { onHand, allocated } }
      const stockMap = {};
      allProds.forEach(s => {
        const pid = s.product_id || s.id;
        stockMap[pid] = { onHand: parseFloat(s.quantity_on_hand) || 0, allocated: 0 };
      });
      (allocatedData || []).forEach(a => {
        if (stockMap[a.product_id]) stockMap[a.product_id].allocated = parseFloat(a.allocated_quantity) || 0;
        else stockMap[a.product_id] = { onHand: 0, allocated: parseFloat(a.allocated_quantity) || 0 };
      });
      setStockLevels(stockMap);

      // Material templates only (items without explicit type default to material)
      const materialTemplates = (templates || []).filter(t => t.type !== 'work');

      const rows = materialTemplates.map(t => {
        const pid = t.recommended_product_id || null;
        const prod = allProds.find(p => String(p.product_id || p.id) === String(pid));
        const factor = parseFloat(prod?.base_units_per_sell_unit) || 1;
        // Stored expected_qty is BASE units → convert to SELL for display.
        const expBase = parseFloat(t.expected_qty);
        const qtySell = (!isNaN(expBase) && expBase > 0) ? String(+(expBase / factor).toFixed(4)) : '';
        // Bundled flag is persisted (is_bundle); legacy rows with no flag fall
        // back to the old cross-category heuristic.
        const showAll = (t.is_bundle !== undefined && t.is_bundle !== null)
          ? !!t.is_bundle
          : (pid ? isCrossCategoryFrom(prod) : false);
        return {
          _key: t.id,
          id: t.id,
          label: t.label || '',
          brand: t.recommended_product_brand || '',
          productId: pid,
          productName: t.recommended_product_name || '',
          qtySell,
          showAll,
          sort_order: t.sort_order || 0
        };
      });

      setItems(rows);
      setOriginalItems(JSON.parse(JSON.stringify(rows)));
    } catch (err) {
      console.error('Error loading item templates:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // category check against a product object (used during load before state set)
  const isCrossCategoryFrom = (prod) => {
    const prodCat = (prod?.category_name || '').toLowerCase();
    return !!prodCat && !!categoryName && prodCat !== categoryName.toLowerCase();
  };

  const addRow = () => {
    setItems(prev => [...prev, {
      _key: `new-${Date.now()}`,
      id: null,
      label: '',
      brand: '',
      productId: null,
      productName: '',
      qtySell: '',
      showAll: false,
      sort_order: prev.length
    }]);
  };

  const removeRow = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const updateRow = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Widening/narrowing the pool invalidates the current brand/product
      if (field === 'showAll') {
        updated[index].brand = '';
        updated[index].productId = null;
        updated[index].productName = '';
      }

      // When brand changes, clear product selection
      if (field === 'brand') {
        updated[index].productId = null;
        updated[index].productName = '';
      }

      // When product changes, update product name
      if (field === 'productId') {
        const productId = value ? parseInt(value, 10) : null;
        updated[index].productId = productId;
        const product = (allProducts || []).find(p => (p.product_id || p.id) === productId);
        updated[index].productName = product ? (product.product_name || product.name) : '';
      }

      return updated;
    });

    if (validationErrors[index]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const validate = () => {
    const errors = {};
    const labels = {};

    items.forEach((item, index) => {
      if (!item.label.trim()) {
        errors[index] = 'Label is required';
        return;
      }
      const lower = item.label.trim().toLowerCase();
      if (labels[lower] !== undefined) {
        errors[index] = 'Duplicate label';
        errors[labels[lower]] = 'Duplicate label';
      }
      labels[lower] = index;
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Sell-unit qty → BASE expected_qty (what the API/server store). Blank ⇒ null.
  const toExpectedBase = (item) => {
    if (item.qtySell === '' || item.qtySell == null) return null;
    const sell = parseFloat(item.qtySell);
    if (isNaN(sell) || sell <= 0) return null;
    const { factor } = packaging(item.productId);
    return +(sell * factor).toFixed(4);
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const originalMap = {};
      originalItems.forEach(o => { if (o.id) originalMap[o.id] = o; });

      const currentIds = new Set(items.filter(i => i.id).map(i => i.id));

      // Delete removed items
      for (const orig of originalItems) {
        if (orig.id && !currentIds.has(orig.id)) {
          await deleteServiceItemTemplate(orig.id);
        }
      }

      // Create new items, update changed items
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const expectedBase = toExpectedBase(item);
        if (!item.id) {
          await createServiceItemTemplate(masterServiceId, {
            label: item.label.trim(),
            type: 'material',
            recommended_product_id: item.productId || null,
            recommended_product_name: item.productName || null,
            recommended_product_brand: item.brand || null,
            expected_qty: expectedBase,
            is_bundle: item.showAll ? 1 : 0,
            sort_order: i
          });
        } else {
          const orig = originalMap[item.id];
          if (orig &&
            (orig.label !== item.label.trim() ||
              orig.brand !== item.brand ||
              orig.productId !== item.productId ||
              orig.qtySell !== item.qtySell ||
              orig.showAll !== item.showAll)) {
            await updateServiceItemTemplate(item.id, {
              label: item.label.trim(),
              recommended_product_id: item.productId || null,
              recommended_product_name: item.productName || null,
              recommended_product_brand: item.brand || null,
              expected_qty: expectedBase,
              is_bundle: item.showAll ? 1 : 0
            });
          }
        }
      }

      if (onTemplatesChanged) onTemplatesChanged();
      onHide();
    } catch (err) {
      console.error('Error saving item templates:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-sharp fa-regular fa-cube mr-2"></i>
          Items for {categoryName} {serviceName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="mr-2" />
            Loading...
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <>
            <p className="text-muted small mb-3">
              Define the product items for this service. Each item represents a section/area that needs
              a product (e.g., "Windshield"), or a <strong>bundled product</strong> included with the package
              (tick "Bundled" to include a product — it can be from any category — handed to the customer at $0).
            </p>

            {items.length === 0 ? (
              <div className="text-center text-muted py-3">
                No product items defined yet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: '26%' }}>Label</th>
                      <th style={{ width: '20%' }}>Brand</th>
                      <th style={{ width: '30%' }}>Product</th>
                      <th style={{ width: '16%' }}>Qty</th>
                      <th style={{ width: '8%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const { brands, byBrand } = poolFor(item.showAll);
                      const pk = packaging(item.productId);
                      const sellQ = parseFloat(item.qtySell);
                      return (
                        <tr key={item._key}>
                          <td>
                            <Form.Control
                              size="sm"
                              type="text"
                              value={item.label}
                              onChange={(e) => updateRow(index, 'label', e.target.value)}
                              placeholder="e.g., Windshield"
                              isInvalid={!!validationErrors[index]}
                            />
                            {validationErrors[index] && (
                              <Form.Text className="text-danger">{validationErrors[index]}</Form.Text>
                            )}
                            <Form.Check
                              type="checkbox"
                              id={`anycat-${index}`}
                              className="mt-1"
                              checked={!!item.showAll}
                              onChange={(e) => updateRow(index, 'showAll', e.target.checked)}
                              label={<small className="text-muted">Bundled / included product</small>}
                            />
                          </td>
                          <td>
                            <Form.Control
                              as="select"
                              size="sm"
                              value={item.brand}
                              onChange={(e) => updateRow(index, 'brand', e.target.value)}
                            >
                              <option value="">--</option>
                              {brands.map(b => (
                                <option key={b} value={b}>{b}</option>
                              ))}
                            </Form.Control>
                          </td>
                          <td>
                            <Form.Control
                              as="select"
                              size="sm"
                              value={item.productId || ''}
                              onChange={(e) => updateRow(index, 'productId', e.target.value)}
                              disabled={!item.brand}
                            >
                              <option value="">--</option>
                              {(byBrand[item.brand] || []).map(p => {
                                const pid = p.product_id || p.id;
                                const pname = p.product_name || p.name;
                                const stock = stockLevels[pid];
                                const bu = unitName(p.unit_id);
                                const unit = bu ? ` ${bu}` : '';
                                const stockText = stock ? ` (${stock.onHand}${unit} in stock)` : '';
                                return (
                                  <option key={pid} value={pid}>{pname}{stockText}</option>
                                );
                              })}
                            </Form.Control>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <Form.Control
                                size="sm"
                                type="number"
                                min="0"
                                step="any"
                                style={{ width: '64px' }}
                                value={item.qtySell}
                                onChange={(e) => updateRow(index, 'qtySell', e.target.value)}
                                disabled={!item.productId}
                                placeholder="—"
                              />
                              {item.productId && pk.sellName && (
                                <small className="text-muted ml-1">{pk.sellName}</small>
                              )}
                            </div>
                            {item.productId && pk.factor > 1 && sellQ > 0 && (
                              <small className="text-muted">= {+(sellQ * pk.factor).toFixed(2)} {pk.baseName}</small>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0"
                              onClick={() => removeRow(index)}
                              title="Remove"
                            >
                              <i className="fa-sharp fa-regular fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              className="btn btn-sm btn-outline-primary mt-2"
              onClick={addRow}
            >
              <i className="fa-sharp fa-regular fa-plus mr-1"></i> Add Item
            </button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading || saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="mr-1" />
              Saving...
            </>
          ) : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductItemTemplateModal;
